import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  api,
  type DatabaseConnectionsReport,
  type DatabaseLocksReport,
  type DatabaseStorageReport,
  type DatabaseTablesReport,
} from "@/lib/api";
import { ErrorBox, PageHeader, Spinner, StatTile } from "@/components/ui";

type Tab = "storage" | "connections" | "tables" | "locks";
const TABS: { key: Tab; label: string }[] = [
  { key: "storage", label: "Storage" },
  { key: "connections", label: "Connections" },
  { key: "tables", label: "Tables & Indexes" },
  { key: "locks", label: "Locks & Transactions" },
];

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) n = 0;
  const units = ["B", "KiB", "MiB", "GiB", "TiB"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return i === 0 ? `${Math.round(v)} ${units[i]}` : `${v.toFixed(1)} ${units[i]}`;
}

export function ObservabilityDatabasePage() {
  const { id = "" } = useParams<{ id: string }>();
  const [tab, setTab] = useState<Tab>("storage");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [storage, setStorage] = useState<DatabaseStorageReport | null>(null);
  const [connections, setConnections] = useState<DatabaseConnectionsReport | null>(null);
  const [tables, setTables] = useState<DatabaseTablesReport | null>(null);
  const [locks, setLocks] = useState<DatabaseLocksReport | null>(null);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      setLoading(true);
      try {
        const [s, c, t, l] = await Promise.all([
          api.databaseStorage(id),
          api.databaseConnections(id),
          api.databaseTables(id),
          api.databaseLocks(id),
        ]);
        setStorage(s.data);
        setConnections(c.data);
        setTables(t.data);
        setLocks(l.data);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <Spinner label="Loading database observability…" />;

  return (
    <div>
      <PageHeader
        eyebrow="Observability"
        title="Database"
        description="Project database storage, live connections, and table stats. Physical node metrics remain clearly labelled."
      />
      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}
      <nav className="mb-6 flex gap-0 overflow-x-auto border-b border-border-standard">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-[color,border-color] duration-fast ease-standard ${
              tab === t.key
                ? "border-secondary text-secondary"
                : "border-transparent text-text-muted hover:text-text-primary"
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>
      {tab === "storage" && <StorageTab data={storage} />}
      {tab === "connections" && <ConnectionsTab data={connections} />}
      {tab === "tables" && <TablesTab data={tables} />}
      {tab === "locks" && <LocksTab data={locks} />}
    </div>
  );
}

function StorageTab({ data }: { data: DatabaseStorageReport | null }) {
  if (!data) {
    return <p className="text-sm text-text-muted">No storage data.</p>;
  }
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Schema size" value={formatBytes(data.total_bytes)} hint={data.schema_name} />
        <StatTile label="Table data" value={formatBytes(data.table_bytes)} />
        <StatTile label="Indexes" value={formatBytes(data.index_bytes)} />
        <StatTile label="Relations" value={data.relation_count} hint={data.attribution} />
      </div>
      {data.largest_tables.length > 0 && (
        <div className="card p-4">
          <h3 className="mb-3 font-sans text-sm font-semibold text-text-primary">Largest tables</h3>
          <table className="data w-full text-sm">
            <thead>
              <tr>
                <th>Table</th>
                <th className="text-right">Rows</th>
                <th className="text-right">Total</th>
                <th className="text-right">Data</th>
                <th className="text-right">Indexes</th>
              </tr>
            </thead>
            <tbody>
              {data.largest_tables.map((t) => (
                <tr key={t.name}>
                  <td className="font-mono text-xs">{t.name}</td>
                  <td className="text-right tabular-nums">{(t.estimated_rows ?? 0).toLocaleString()}</td>
                  <td className="text-right tabular-nums">{formatBytes(t.total_bytes)}</td>
                  <td className="text-right tabular-nums">{formatBytes(t.table_bytes ?? 0)}</td>
                  <td className="text-right tabular-nums">{formatBytes(t.index_bytes ?? 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data.largest_indexes.length > 0 && (
        <div className="card p-4">
          <h3 className="mb-3 font-sans text-sm font-semibold text-text-primary">Largest indexes</h3>
          <table className="data w-full text-sm">
            <thead>
              <tr>
                <th>Index</th>
                <th className="text-right">Size</th>
              </tr>
            </thead>
            <tbody>
              {data.largest_indexes.map((ix) => (
                <tr key={ix.name}>
                  <td className="font-mono text-xs">{ix.name}</td>
                  <td className="text-right tabular-nums">{formatBytes(ix.total_bytes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data.growth.length > 0 && (
        <div className="card p-4">
          <h3 className="mb-3 font-sans text-sm font-semibold text-text-primary">Size history</h3>
          <p className="mb-2 text-xs text-text-muted">Snapshots from the schema collector</p>
          <table className="data w-full text-sm">
            <thead>
              <tr>
                <th>Captured</th>
                <th className="text-right">Total</th>
                <th className="text-right">Tables</th>
                <th className="text-right">Indexes</th>
              </tr>
            </thead>
            <tbody>
              {data.growth.slice(-12).map((g) => (
                <tr key={g.captured_at}>
                  <td className="text-xs text-text-muted">{new Date(g.captured_at).toLocaleString()}</td>
                  <td className="text-right tabular-nums">{formatBytes(g.total_bytes)}</td>
                  <td className="text-right tabular-nums">{formatBytes(g.table_bytes)}</td>
                  <td className="text-right tabular-nums">{formatBytes(g.index_bytes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ConnectionsTab({ data }: { data: DatabaseConnectionsReport | null }) {
  if (!data) {
    return <p className="text-sm text-text-muted">No connection data.</p>;
  }
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Project sessions" value={data.total_project_sessions} hint={data.attribution} />
        <StatTile label="Active" value={data.active_sessions} />
        <StatTile label="Idle in xact" value={data.idle_in_transaction} />
        <StatTile
          label="Database utilization"
          value={`${data.utilization_pct.toFixed(1)}%`}
          hint={`${data.host_total_sessions}/${data.max_connections} (project database)`}
        />
      </div>
      <p className="text-xs text-text-muted">{data.shared_host_note}</p>
      {data.sessions.length > 0 ? (
        <div className="card p-4">
          <h3 className="mb-3 font-sans text-sm font-semibold text-text-primary">Project sessions</h3>
          <div className="max-h-96 overflow-y-auto">
            <table className="data w-full text-sm">
              <thead>
                <tr>
                  <th>PID</th>
                  <th>State</th>
                  <th>App</th>
                  <th>User</th>
                  <th>Query</th>
                </tr>
              </thead>
              <tbody>
                {data.sessions.map((s) => (
                  <tr key={s.pid}>
                    <td className="tabular-nums">{s.pid}</td>
                    <td>
                      <span className="mono-chip">{s.state || "—"}</span>
                    </td>
                    <td className="max-w-[120px] truncate font-mono text-xs">{s.application_name || "—"}</td>
                    <td className="font-mono text-xs">{s.user_name}</td>
                    <td className="max-w-[200px] truncate font-mono text-xs text-text-muted">{s.query || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <p className="text-sm text-text-muted">
          No project-attributed sessions right now. Sessions appear when connections set{" "}
          <code className="mono-chip">application_name</code> with project scope.
        </p>
      )}
    </div>
  );
}

function TablesTab({ data }: { data: DatabaseTablesReport | null }) {
  if (!data) {
    return <p className="text-sm text-text-muted">No table stats.</p>;
  }
  return (
    <div className="space-y-5">
      <p className="text-xs text-text-muted">
        Schema <span className="mono-chip">{data.schema_name}</span> · {data.attribution}
      </p>
      {data.tables.length === 0 ? (
        <p className="text-sm text-text-muted">No user tables in this schema yet.</p>
      ) : (
        <div className="card p-4">
          <div className="max-h-[32rem] overflow-auto">
            <table className="data w-full text-sm">
              <thead>
                <tr>
                  <th>Table</th>
                  <th className="text-right">Rows</th>
                  <th className="text-right">Size</th>
                  <th className="text-right">Seq</th>
                  <th className="text-right">Idx</th>
                  <th className="text-right">Dead</th>
                  <th>Last vacuum</th>
                  <th>Hint</th>
                </tr>
              </thead>
              <tbody>
                {data.tables.map((t) => (
                  <tr key={t.table_name}>
                    <td className="font-mono text-xs">{t.table_name}</td>
                    <td className="text-right tabular-nums">{t.estimated_rows.toLocaleString()}</td>
                    <td className="text-right tabular-nums">{formatBytes(t.total_bytes)}</td>
                    <td className="text-right tabular-nums">{t.seq_scan.toLocaleString()}</td>
                    <td className="text-right tabular-nums">{t.idx_scan.toLocaleString()}</td>
                    <td className="text-right tabular-nums">{t.n_dead_tup.toLocaleString()}</td>
                    <td className="text-xs text-text-muted">
                      {t.last_autovacuum
                        ? new Date(t.last_autovacuum).toLocaleString()
                        : t.last_vacuum
                          ? new Date(t.last_vacuum).toLocaleString()
                          : "—"}
                    </td>
                    <td className="text-xs text-amber-700">{t.dead_tuple_hint || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function LocksTab({ data }: { data: DatabaseLocksReport | null }) {
  if (!data) {
    return <p className="text-sm text-text-muted">No locks data.</p>;
  }
  return (
    <div className="space-y-5">
      <p className="text-xs text-text-muted">{data.note}</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatTile label="Blocking pairs" value={data.blocking.length} />
        <StatTile label="Long transactions" value={data.long_transactions.length} />
        <StatTile label="Idle in xact" value={data.idle_in_transaction} />
      </div>
      {data.blocking.length > 0 && (
        <div className="card p-4">
          <h3 className="mb-3 font-sans text-sm font-semibold text-text-primary">Blocking tree</h3>
          <table className="data w-full text-sm">
            <thead>
              <tr>
                <th>Blocked PID</th>
                <th>Blocking PID</th>
                <th>Blocked query</th>
                <th>Blocking query</th>
              </tr>
            </thead>
            <tbody>
              {data.blocking.map((b, i) => (
                <tr key={i}>
                  <td className="tabular-nums">{b.blocked_pid}</td>
                  <td className="tabular-nums">{b.blocking_pid}</td>
                  <td className="max-w-[180px] truncate font-mono text-xs">{b.blocked_query}</td>
                  <td className="max-w-[180px] truncate font-mono text-xs">{b.blocking_query}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data.long_transactions.length > 0 ? (
        <div className="card p-4">
          <h3 className="mb-3 font-sans text-sm font-semibold text-text-primary">
            Long transactions (&gt;30s)
          </h3>
          <table className="data w-full text-sm">
            <thead>
              <tr>
                <th>PID</th>
                <th>State</th>
                <th className="text-right">Age (s)</th>
                <th>Query</th>
              </tr>
            </thead>
            <tbody>
              {data.long_transactions.map((x) => (
                <tr key={x.pid}>
                  <td className="tabular-nums">{x.pid}</td>
                  <td>
                    <span className="mono-chip">{x.state}</span>
                  </td>
                  <td className="text-right tabular-nums">{x.age_seconds.toFixed(0)}</td>
                  <td className="max-w-[240px] truncate font-mono text-xs">{x.query}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-text-muted">No long-running transactions right now.</p>
      )}
    </div>
  );
}
